import { OrderDetailAttributes } from "@albatrosdeveloper/ave-models-package";
import {
  andAllWhere,
  andWhere,
  buildQuery,
  CombinedFilter,
  noop,
  Ops,
  orderBy,
  OrderVariant,
  orWhere,
  page,
  where,
} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { IPagination, IPaginationResult } from "app/common/interfaces";
import { RequestOptions } from "app/common/interfaces/options.interface";
import { AbstractService } from "app/services/abstract.service";
import { Observable } from "rxjs";
import { environment as env } from "../../../../../../environments/environment.prod";
import { IFilters } from "app/modules/components/filters/interfaces/filterInterface";

const apiUrl = env.API_AVE_ORDER_REPORT_URL;
const moduleName = "order_report";

@Injectable({
  providedIn: "root",
})
export class DeliveryIncompleteService extends AbstractService<
  OrderDetailAttributes,
  OrderDetailAttributes,
  OrderDetailAttributes
> {
  /** The last filter used to load the list */
  currentFilter: CombinedFilter<OrderDetailAttributes>;

  constructor(http: HttpClient) {
    super(apiUrl, moduleName, http);
  }

  findByFilter(
    filter: CombinedFilter<OrderDetailAttributes>,
    options?: RequestOptions
  ) {
    const API_URL: string =
      apiUrl + `api/${moduleName}/lost_sale`;
    const headers: Record<string, any> = {};
    // see order-customer-export for more info about this header
    // ctx: stands for context
    if (options?.contextHeader) headers["x-ave-ctx"] = options.contextHeader;
    return this.http.post<IPaginationResult<OrderDetailAttributes>>(API_URL, {
      filter: filter
    },{
      headers,
    });
  }

  /**
   * Pagination
   * @param options Pagination options
   * @returns Paginated data
   */
  findAllWithCount(
    options: IPagination<OrderDetailAttributes>,
    dataToFilter: IFilters
  ): Observable<IPaginationResult<OrderDetailAttributes>> {
    const query = buildQuery<OrderDetailAttributes>(
      where("_deleted", Ops.eq(false)),
      andWhere(<any>"serial", Ops.like(options.search ?? "", "i")),
      orWhere(
        <any>"user.user_login.username",
        Ops.like(options.search ?? "", "i")
      ),
      orWhere(
        <any>"subRoute.externalKeys.code",
        Ops.like(options.search ?? "", "i")
      ),
      orWhere(<any>"subRoute.name", Ops.like(options.search ?? "", "i")),
      orWhere(
        <any>"relatedDocuments.externalDocument",
        Ops.in([Ops.like(options.search ?? "", "i")])
      ), //
      orWhere(
        <any>"shipTo.externalKeys.code",
        Ops.like(options.search ?? "", "i")
      ),
      orWhere(<any>"shipTo.name", Ops.like(options.search ?? "", "i")),
      orWhere(
        "detail.material.code" as keyof unknown,
        Ops.like(options.search ?? "", "i")
      ),
      orWhere(
        "detail.material.name" as keyof unknown,
        Ops.like(options.search ?? "", "i")
      ),
      dataToFilter.startDate && dataToFilter.endDate
        ? andAllWhere(
            "createdAt",
            Ops.btwnDates(dataToFilter.startDate, dataToFilter.endDate)
          )
        : noop(),

      dataToFilter.startTrip && dataToFilter.endTrip
        ? andAllWhere(
            "trip.dateHourStart" as keyof OrderDetailAttributes,
            Ops.btwnDates(dataToFilter.startTrip, dataToFilter.endTrip)
          )
        : noop(),
      dataToFilter.end_startTrip && dataToFilter.end_endTrip
        ? andAllWhere(
            "trip.dateHourEnd" as keyof OrderDetailAttributes,
            Ops.btwnDates(dataToFilter.end_startTrip, dataToFilter.end_endTrip)
          )
        : noop(),
      dataToFilter.warehouseId && dataToFilter.warehouseId.length > 0
        ? andAllWhere(
            "warehouses.id" as keyof OrderDetailAttributes,
            Ops.in(...dataToFilter.warehouseId)
          )
        : noop(),
      dataToFilter.startDeliveryDate && dataToFilter.endDeliveryDate
        ? andAllWhere(
            "deliveryDateTime" as keyof OrderDetailAttributes,
            Ops.btwnDates(
              dataToFilter.startDeliveryDate,
              dataToFilter.endDeliveryDate
            )
          )
        : noop(),
      dataToFilter.subRouteId && dataToFilter.subRouteId.length > 0
        ? andAllWhere(
            "subRoute.id" as keyof OrderDetailAttributes,
            Ops.in(...dataToFilter.subRouteId)
          )
        : noop(),
      dataToFilter.customerId && dataToFilter.customerId.length > 0
        ? andAllWhere(
            "shipTo.id" as keyof OrderDetailAttributes,
            Ops.in(...dataToFilter.customerId)
          )
        : noop(),
      dataToFilter.materialId && dataToFilter.materialId.length > 0
        ? andAllWhere(
            "detail.material.code" as keyof OrderDetailAttributes,
            Ops.in(...dataToFilter.materialId)
          )
        : noop(),
      dataToFilter.materialGroupId && dataToFilter.materialGroupId.length > 0
        ? andAllWhere(
            "detail.material.materialGroups.code" as keyof OrderDetailAttributes,
            Ops.in(...dataToFilter.materialGroupId)
          )
        : noop(),
      dataToFilter.seller && dataToFilter.seller.length > 0
        ? andAllWhere(
            "user.user_login.username" as keyof OrderDetailAttributes,
            Ops.in(...dataToFilter.seller)
          )
        : noop(),
      dataToFilter.status && dataToFilter.status.length > 0
        ? andAllWhere(
            "statusId" as keyof OrderDetailAttributes,
            Ops.in(...dataToFilter.status)
          )
        : noop(),
      dataToFilter.statusERP && dataToFilter.statusERP.length > 0
        ? andAllWhere(
            "statusERP" as keyof OrderDetailAttributes,
            Ops.in(...dataToFilter.statusERP)
          )
        : noop(),
      dataToFilter.typeCustomer && dataToFilter.typeCustomer.length > 0
        ? andAllWhere(
            "shipTo.types.code" as keyof OrderDetailAttributes,
            Ops.in(...dataToFilter.typeCustomer)
          )
        : noop(),
      andAllWhere("detail.statusId" as keyof OrderDetailAttributes, Ops.eq(10)),

      orderBy(
        options.sort ?? "createdAt",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    this.currentFilter = query;

    const API_URL: string = apiUrl + `api/${moduleName}/lost_sale`;
    return this.http.post<IPaginationResult<OrderDetailAttributes>>(API_URL, {
      filter: query
    });
  }
}
